// Ads-Manager-style clear-zone guardrail — translucent yellow bands
// marking the regions the platform's own UI will cover.
//
// Distinct from the chrome overlay: the chrome shows what the UI looks
// like, this shows where you must not put anything that matters. Only
// meaningful on the full-screen placements; the card placements draw
// their furniture around the creative, not on it, so there is nothing
// to clear. (The AdChoices roundel Google stamps on Display/Discover
// creative is the one exception, and it is a ~3% corner square — too
// small to be worth a band, and it is already drawn literally by
// <GoogleAdMarkers>.)
//
// ⚠️ THE VERTICAL BANDS SHOULD COME FROM THE BACKEND WHEN THEY CAN.
// `services/platformFormats.js` declares a real safe area per format — the
// same clamp the renderer applies to burned-in titling — and
// GET /api/ads/formats exposes it, already normalised, as `safeAreaPct`.
// Pass it and the top/bottom bands are drawn from the actual clamp. A
// guardrail that disagrees with the renderer is worse than no guardrail,
// because it is trusted.
//
// The per-placement percentages below are the FALLBACK for when no
// safeArea is available (Title Studio previews a brand-level spec, not an
// ad; an older backend omits the field). They are frontend-side
// approximations of where the platform UI sits, calibrated by eye.
//
// Side bands and the right action rail are always local: they describe
// horizontal furniture, and `safeArea` only models top/bottom.

import type { CSSProperties } from 'react';
import type { ChromeKind } from './types';

// Reserved bands as FRACTIONS of frame height (0..1), straight from
// GET /api/ads/formats `safeAreaPct`. Fractions rather than pixels on
// purpose: the backend's raw `safeArea` is in CANVAS space, not delivery
// space, and a consumer that divides by the wrong height understates the
// band by half. Normalising server-side is the only place that is
// unambiguous, so this type deliberately cannot express pixels.
export type SafeAreaPct = { top: number; bottom: number };

const FILL = 'rgba(250,204,21,.28)';
const EDGE = '1px dashed rgba(250,204,21,.85)';

// top / side / bottom / right-rail, as fractions of the frame.
const BANDS: Partial<Record<ChromeKind, { top: string; side: string; bottom: string; rail?: string }>> = {
  // Top/side insets match the render-time vertical safe zone so the
  // guardrail never understates the clamp the renderer applies.
  // Stories' bottom band (20%) is intentionally looser than Reels'
  // (35%): Reels' caption + CTA stack is much taller.
  reels:   { top: '14%', side: '7.5%', bottom: '35%', rail: '12.5%' },
  stories: { top: '14%', side: '7.5%', bottom: '20%' },

  // Shorts reserves MORE at the bottom than Reels: the identity row,
  // the description line and a full-width filled CTA button all stack
  // there, and the action rail carries an extra dislike slot so it runs
  // taller as well.
  'yt-shorts': { top: '10%', side: '6%', bottom: '38%', rail: '13%' },

  // In-stream's transport controls fade out, so the durable coverage is
  // the bottom strip only — advertiser card, Ad badge, Skip button and
  // the yellow progress bar. No side bands: nothing persists there.
  'yt-instream': { top: '0%', side: '0%', bottom: '18%' }
};

export function SafeZoneGuardrail({
  kind,
  safeArea
}: {
  kind:       ChromeKind;
  safeArea?:  SafeAreaPct | null;
}) {
  const spec = BANDS[kind];
  // No local spec means a card placement (nothing is drawn over the
  // creative) — and a safeArea alone must not conjure one, or a Feed preview
  // would sprout bands for a clamp that only applies to video titling.
  if (!spec) return null;

  const { side, rail } = spec;

  // Real clamp wins over the eyeballed default. Still validated here rather
  // than trusted: a NaN or out-of-range fraction reaching the style would
  // paint a band over the whole creative, which reads as "none of this frame
  // is safe" — a worse lie than the approximation it replaced.
  const frac = (v: number | undefined) =>
    Number.isFinite(v) ? Math.max(0, Math.min(1, v as number)) * 100 : null;
  const fromApi = safeArea
    ? { top: frac(safeArea.top), bottom: frac(safeArea.bottom) }
    : null;
  const usable = !!fromApi && fromApi.top !== null && fromApi.bottom !== null;

  // Kept as NUMBERS through the zero-check and only formatted at the end.
  // Comparing the formatted string against '0%' silently failed for the
  // computed path, which produces '0.00%' — so a format with no reserved
  // top (safeArea.top === 0) drew a zero-height band whose dashed border
  // still painted a hairline across the top of the creative.
  const topPct    = usable ? (fromApi as { top: number }).top       : parseFloat(spec.top);
  const bottomPct = usable ? (fromApi as { bottom: number }).bottom  : parseFloat(spec.bottom);
  const sidePct   = parseFloat(side);
  const top    = `${topPct}%`;
  const bottom = `${bottomPct}%`;

  const band = (extra: CSSProperties): CSSProperties => ({
    position: 'absolute',
    background: FILL,
    pointerEvents: 'none',
    boxSizing: 'border-box',
    ...extra
  });

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 6 }} aria-hidden>
      {topPct > 0 && <div style={band({ top: 0, left: 0, right: 0, height: top, borderBottom: EDGE })} />}
      {bottomPct > 0 && <div style={band({ bottom: 0, left: 0, right: 0, height: bottom, borderTop: EDGE })} />}
      {sidePct > 0 && (
        <>
          <div style={band({ top, bottom, left: 0, width: side, borderRight: EDGE })} />
          <div style={band({ top, bottom, right: 0, width: side, borderLeft: EDGE })} />
        </>
      )}
      {/* The right action rail eats a further column on Reels and Shorts. */}
      {rail && <div style={band({ top, bottom, right: 0, width: rail, borderLeft: EDGE })} />}
    </div>
  );
}
